import type { SectionStatus } from '@/shell/forms/FormKit'
import type { ChecklistItem, IntakeDocument } from '../api/onboarding-types'
import type { DocumentType } from '../api/types'

/** The numbered sections of the new-product page, in order, with the checklist groups each one owns. */
export const ONBOARDING_SECTIONS = [
  { id: 'identity', title: 'Identity', description: 'What the product is called and where it sits in Odoo.', groups: ['identity'] },
  { id: 'units', title: 'Units & pack', description: 'How it is stocked, bought and packed.', groups: ['units'] },
  { id: 'vendor', title: 'Vendor', description: 'Who we buy it from, the minimum order and what it costs.', groups: ['vendor'] },
  { id: 'pricing', title: 'Pricing', description: 'The public price is set automatically at cost + 30%.', groups: ['pricing'] },
  { id: 'regulatory', title: 'Regulatory data', description: 'INCI, CAS, origin, HS code and hazmat.', groups: ['regulatory'] },
  { id: 'documents', title: 'Documents', description: 'Sample CofA and SDS are required; add any others the vendor sent.', groups: ['documents', 'crosscheck'] },
  { id: 'notes', title: 'Notes', description: 'Anything the reviewers should know.', groups: [] },
] as const

export type SectionId = (typeof ONBOARDING_SECTIONS)[number]['id']

export type { SectionStatus }

export function sectionStatus(items: ChecklistItem[], groups: readonly string[]): SectionStatus | null {
  const rows = items.filter((i) => groups.includes(i.group))
  if (!rows.length) return null
  const missing = rows.filter((i) => !i.ok && i.severity !== 'warning').length
  const warnings = rows.filter((i) => !i.ok && i.severity === 'warning').length
  return { missing, warnings, complete: missing === 0 }
}

/** Fields the server checklist requires (errors block submit). Mirrors completeness_report;
 * internal reference, category and HS code are optional (Chris 2026-10-09). */
export const REQUIRED_FIELDS = new Set([
  'name', 'uom', 'uom_po', 'pack_size', 'inci', 'country',
])

const FIELD_FOR_KEY: Record<string, string> = {
  name: 'name',
  default_code: 'default_code',
  category: 'categ',
  duplicate: 'name',
  similar: 'name',
  uom: 'uom',
  uom_po: 'uom_po',
  uom_category: 'uom_po',
  uom_change: 'uom',
  pack_size: 'pack_size',
  inci: 'inci',
  country: 'country',
  hs_code: 'hs_code',
  hazmat: 'hazmat',
  unit_cost: 'vendor-1',
  tier_order: 'tiers',
  below_cost: 'tiers',
  list_below_cost: 'list_price',
}

/** DOM id to scroll to for a checklist item: the field when we know it, else its section. */
export function targetForChecklistKey(key: string, group: string): string {
  if (FIELD_FOR_KEY[key]) return `field-${FIELD_FOR_KEY[key]}`
  if (key.startsWith('name_')) return 'field-name'
  const vendor = /^vendor_(\d+)/.exec(key)
  if (vendor) return `field-vendor-${vendor[1]}`
  if (key.startsWith('tier_')) return 'field-tiers'
  const section = ONBOARDING_SECTIONS.find((s) => (s.groups as readonly string[]).includes(group))
  return `section-${section?.id ?? 'identity'}`
}

export { jumpTo } from '@/shell/forms/jump'

export interface DocChip {
  key: string
  label: string
  state: 'done' | 'missing'
  required: boolean
}

/**
 * One chip per required type (done or missing) plus one per other uploaded
 * type (TDS, statements…). Chris 2026-10-09: "If additional documents are
 * uploaded they should get a chip also."
 */
export function documentChips(
  types: Pick<DocumentType, 'key' | 'label' | 'required_for_onboarding' | 'applies_to'>[],
  docs: Pick<IntakeDocument, 'doc_type_key' | 'doc_type_label'>[],
  spiBranded: boolean,
): DocChip[] {
  const present = new Map(docs.map((d) => [d.doc_type_key, d.doc_type_label]))
  const required = types.filter(
    (t) => t.required_for_onboarding && (t.applies_to === 'all' || (t.applies_to === 'spi_branded') === spiBranded),
  )
  const chips: DocChip[] = required.map((t) => ({
    key: t.key, label: t.label, required: true, state: present.has(t.key) ? 'done' : 'missing',
  }))
  const seen = new Set(chips.map((c) => c.key))
  for (const [key, label] of present) {
    if (!seen.has(key)) chips.push({ key, label, required: false, state: 'done' })
  }
  return chips
}
